import React, { useState, useEffect } from "react";
import SearchForm from "../../stories/Forms/SearchForm";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import {
  links,
  searchFormFields,
  clinicalPatientFields,
  vitalitySearchFormFields,
} from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import Table from "../../stories/Tables/Table";
import formatData, {
  formatReportedDate,
  statusMapping,
  formatDtAge,
  flattenObject,
  STATUS_MAP,
  formatDateTime,
} from "../../utils/utils";
import { Primary } from "../../stories/Buttons/Button.stories";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../components/AuthContext/AuthContext";
import useApis from "../../services/useAPI";
import {
  searchBookingsUrl,
  patientSearchApi,
  storeLocatorePostApi,
  repeatedTestMapping
} from "../../utils/constants";
import { XCircleFill } from "react-bootstrap-icons";
import PageLayout from "../../components/Layout/PageLayout";
import VitalityDuplicatedTable from "../../stories/Tables/VitalityDuplicatedTable";
import DetailCard from "../../stories/Cards/DetailCard";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import VHConfirmationPopup from "../../stories/Modals/VHConfirmationPopup";
import infoBootsIcon from "../../assets/icons/boots-info-grey.svg"

const formatNoteTooltip = (repeatedTest, notes) => {
  if (notes && typeof notes === "string" && notes.trim()) {
    return notes.trim();
  }
  const text = String(repeatedTest || "").toLowerCase();
  if (text.includes("payment required")) {
    return "Repeat test. Payment required.";
  }
  if (
    text.includes("paid") ||
    text.includes("payment not required") ||
    text.includes("already paid")
  ) {
    return "Repeat test. Payment not required.";
  }
  if (text.startsWith("yes")) {
    const detail = String(repeatedTest)
      .replace(/^yes[;:,\s-]*/i, "")
      .replace(/^\(|\)$/g, "")
      .trim();
    if (detail) {
      return `Repeat test. ${detail}.`;
    }
    return "Repeat test.";
  }
  return repeatedTest ? `Repeat test. ${repeatedTest}.` : "Repeat test.";
};

const SERVICE_CONFIG = {
  healthCare: {
    pageTitle: "Healthcare Services",
    fields: searchFormFields,
  },
  clinical: {
    pageTitle: "PATIENT RECORD",
    fields: clinicalPatientFields,
  },
};

// ─── Pure Helpers (outside component — no hooks) ──────────────────────────────

/**
 * Returns className and label for a given booking status string.
 */
const getStatusDetails = (status, resultTs, isVhLogin = false) => {
  if (!status) return { className: "", label: "" };
  if (status === "rejected" && resultTs != null) {
    status = "error";
  }
  if (isVhLogin && status === "registered") {
    return { className: "", label: "Registered" };
  }
  const key = status.toLowerCase().replace(/\s+/g, "-");
  return STATUS_MAP[key] ?? { className: "", label: status };
};

/**
 * Resolves userData from the various location.state shapes.
 */
const resolveUserData = (state, isVhLogin = false) => {
  const raw =
    state?.user ??
    state?.resultPost?.user_info ??
    state?.data?.user_info ??
    null;

  if (!raw) return null;

  const dob =
    state?.user?.dob ??
    state?.resultPost?.user_info?.dob ??
    state?.data?.dob ??
    null;

  return {
    ...raw,
    dob: dob ? formatDtAge(dob, isVhLogin) : null,
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PatientDetails component displays details of a patient record.
 * It includes a search form, a table for displaying modified bookings, and a back button.
 */
function PatientDetails() {
  const auth = useAuth();
  const selectedService = localStorage.getItem("selectedService");
  const serviceConfig =
    SERVICE_CONFIG[selectedService] ?? SERVICE_CONFIG.clinical;

  auth.setTitle(serviceConfig.pageTitle);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    callApi,
    resultPost,
    serverError,
    tokenError,
    toggleShowError,
    isLoading,
  } = useApis();

  const {
    callApi: callStoreLocatorApi,
    serverError: storeLocatorError,
    resultGet: storeLocatorResult,
  } = useApis(storeLocatorePostApi);

  const isVhLogin = auth?.featureFlag && selectedService === "healthCare";
  const fields = isVhLogin ? vitalitySearchFormFields.filter((field) => field.name !== "booking_id") : serviceConfig.fields;
  const [isPageReady, setIsPageReady] = useState(false);
  const [storeLocatorLoading, setStoreLocatorLoading] = useState(false);

  const stages = [
    "patient-record",
    "patient-details",
    "confirm-details",
    "enter-barcode",
    "appointment-confirmation",
    "clinical-screening",
    "clinical-measurement",
  ];

  useEffect(() => {
    if (
      !stages.includes(sessionStorage.getItem("allowedStage")) &&
      auth?.featureFlag
    ) {
      navigate("/patient-record");
    }
  }, [sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  useEffect(() => {
    const hasStateData =
      location.state?.resultPost?.bookings || location.state?.user;
    if (hasStateData) {
      setIsPageReady(true);
      return;
    }
    if (resultPost !== undefined || serverError) {
      setIsPageReady(true);
    }
  }, [resultPost, serverError, location.state]);

  useEffect(() => {
    if (serverError || tokenError) {
      auth?.showToast("Error", tokenError || serverError, "../assets/mhc.png", {
        icon: <XCircleFill />,
        className: auth?.featureFlag && "vh-toaster",
      });
    }
  }, [serverError, tokenError]);

  useEffect(() => {
    localStorage.setItem("patientCard", "false");
  }, []);

  const url = isVhLogin ? patientSearchApi : searchBookingsUrl;

  const userData = resolveUserData(location.state, isVhLogin);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleSubmit = async (data) => {
    const formattedData = data && formatData(data);
    await callApi(formattedData, url, "POST", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
  };

  useEffect(() => {
    if (location.state?.data) {
      handleSubmit(location.state?.data);
    }
  }, [location.state]);

  const handleBackClick = () => navigate(-1);

  // ─── Booking Helpers ────────────────────────────────────────────────────────

  /**
   * Returns true for statuses where row interaction should be disabled.
   */
  const isHoverDisabled = (status, phlebotomistBookingId) =>
    status === "registered" ||
    status === "created" ||
    status === "quarantined" ||
    (status === "activating" && phlebotomistBookingId !== "");

  /**
   * Returns the navigation path for a booking row click.
   */
  const getBookingNavPath = async (booking) => {
    if(isVhLogin){
    if (
      ![
        "registered",
        "processing",
        "results-ready",
        "activating",
        "quarantined",
        "created",
      ].includes(booking.status?.toLowerCase())
    ) {
      return null;
    }

    setStoreLocatorLoading(true);
    try {
      const storeLocatorResponse = await callStoreLocatorApi(
        {
          booking_id: booking.booking_id,
          store_id: JSON.parse(sessionStorage.getItem("selectedStore") || null)
            .store_number,
        },
        storeLocatorePostApi,
        "POST",
      );
      const confirmedData = storeLocatorResponse?.data;

      auth?.allowStage("patient-details");
      auth?.setDisplayUserInfo(displayUserInfo);
      sessionStorage.setItem("serviceType", booking?.type);
      if (
        booking.service_name === "Vitality Healthcheck" ||
        booking.service_name === "Heart Health Check"
      ) {
        if (booking.status === "results-ready") {
          return {
            path: "/result-outcome",
            state: {
              bookingId: confirmedData?.booking_id,
              serviceType: confirmedData?.service_type,
              serviceName: confirmedData?.service_name,
              patientSex: confirmedData?.sex,
              displayUserInfo: displayUserInfo,
              fromValidSource: true,
            },
          };
        }

        return {
          path: "/clinical-screening",
          state: {
            bookingId: confirmedData?.booking_id,
            serviceType: confirmedData?.service_type,
            serviceName: confirmedData?.service_name,
            patientSex: confirmedData?.sex,
            displayUserInfo: displayUserInfo,
          },
        };
      }
    } catch (err) {
      auth?.showToast(
        "Error",
        "Could not reach store locator. Please try again.",
        "../assets/mhc.png",
        { icon: <XCircleFill />, className: auth?.featureFlag && "vh-toaster" },
      );
      return null;
    } finally {
      setStoreLocatorLoading(false);
    }
  }

    if (booking.status === "registered") {
      if (auth?.featureFlag) {
        return `/enter-barcode/${booking.phlebotomist_booking_id}`;
      }
      return `/test-confirmation/${booking.phlebotomist_booking_id}`;
    }
    if (booking.status === "quarantined") {
      if (booking.is_activating === true) {
        return `/appointment-confirmation/${booking.phlebotomist_booking_id}`;
      }
      if (auth?.featureFlag && booking.is_activating !== true) {
        return `/enter-barcode/${booking.phlebotomist_booking_id}`;
      }
      return `/test-confirmation/${booking.phlebotomist_booking_id}`;
    }
    return `/appointment-confirmation/${booking.phlebotomist_booking_id}`;
  };

  const modifyBookings = (bookings) =>
    bookings.map((booking, index) => {
      const bookingStatus =
        statusMapping[booking.status?.replace(/-/g, "_")] ?? {};
      const hover = isHoverDisabled(
        booking.status,
        booking.phlebotomist_booking_id,
      );
      const { className, label } = getStatusDetails(
        booking?.status,
        booking?.result_ts,
      );

      return {
        id: index + 1,
        bookingId: booking.booking_id,
        testType: booking.test_type,
        sampleType: booking.sample_type,
        reportedDate: booking.reported_date
          ? formatReportedDate(booking.reported_date)
          : null,
        doctor: booking.reviewer_name ?? " ",
        bookingStatus,
        bookingStatusValue: booking.status,
        status: {
          component: (
            <Primary
              hover={hover}
              label={label}
              onClick={async (e) => {
                if (hover) {
                  const path = await getBookingNavPath(booking);
                  if (path) navigate(path);
                } else {
                  e.preventDefault();
                }
              }}
              size="small"
              variant={className}
            />
          ),
        },
      };
    });
  const vhModifyBookings = (bookings, isVhLogin) =>
    bookings.map((booking, index) => {
      const { label } = getStatusDetails(
        booking?.status,
        booking?.result_ts,
        isVhLogin,
      );

      const selectableStatuses = {
        "Private Phlebotomy": ["registered", "quarantined", "activating"],
        "Vitality Healthcheck": ["registered", "processing", "results-ready"],
        "Heart Health Check": ["registered", "processing", "results-ready"],
      };

      const canSelect = (
        selectableStatuses[booking.service_name] ?? []
      ).includes(booking.status?.toLowerCase());

      return {
        id: index + 1,
        serviceName: booking.service_name,
        bookingId: booking.booking_id,
        repeatedTest: repeatedTestMapping[booking.repeated_test] ?? booking.repeated_test,
        rawRepeatedTest: booking.repeated_test,
        notes: booking.notes,
        testType: booking.type,
        sampleType: booking.sample_type,
        reportedDate: booking.display_timestamp?.trim()
          ? formatDateTime(booking.display_timestamp)
          : null,
        doctor: booking.reviewer_name ?? " ",
        bookingStatusValue: booking.status,
        statusText: label,
        actionButton: canSelect ? (
          <button
            type="button"
            className="vh-table-action-btn"
            onClick={async () => {
              const navigation = await getBookingNavPath(booking);

              if (!navigation) return;

              if (
                booking.service_name === "Vitality Healthcheck" ||
                booking.service_name === "Heart Health Check"
              ) {
                navigate(navigation.path, {
                  state: navigation.state,
                });
              } else {
                navigate(navigation);
              }
              auth?.allowStage("patient-details");
            }}
          >
            Select
          </button>
        ) : null,
      };
    });

  // ─── Derived Data ───────────────────────────────────────────────────────────

  const rawBookings =
    location.state?.resultPost?.bookings ?? resultPost?.bookings ?? [];

  const consultationId = rawBookings?.[0]?.booking_id ?? "";

  const flattenedBookings = rawBookings.map((booking) =>
    flattenObject(booking),
  );
  const modifiedBookings = flattenedBookings.length
    ? isVhLogin
      ? vhModifyBookings(flattenedBookings, isVhLogin)
      : modifyBookings(flattenedBookings)
    : [];

  const userInfo = location.state?.resultPost?.user_info ?? userData;

  const displayUserInfo = {
    ...userInfo,
    booking_id: consultationId,
    sex: userInfo?.sex
      ? userInfo.sex.charAt(0).toUpperCase() + userInfo.sex.slice(1)
      : null,
    ...(location.state?.isDublicated
      ? { dob: userInfo?.dob ? formatDtAge(userInfo?.dob, isVhLogin) : null }
      : { dob: userInfo?.dob }),
  };

  const bookingColumns = [
    {
      dataField: "bookingId",
      text: "Consultation ID",
    },
    {
      dataField: "serviceName",
      text: "Service name",
    },
    {
      dataField: "testType",
      text: "Service type",
    },
    {
      dataField: "repeatedTest",
      text: "Note",
      render: (value, row) => {
        const valStr = String(value || row?.rawRepeatedTest || "").trim();
        const hasNote =
          valStr !== "" &&
          valStr.toLowerCase() !== "no" &&
          valStr.toLowerCase() !== "-";

        if (!hasNote) {
          return "No";
        }

        const tooltipText = formatNoteTooltip(valStr, row?.notes);

        return (
          <OverlayTrigger
            placement="bottom"
            flip={true}
            popperConfig={{
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                    padding: 8,
                  },
                },
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: ["top", "bottom"],
                    padding: 8,
                  },
                },
              ],
            }}
            delay={{ show: 200, hide: 100 }}
            overlay={
              <Tooltip
                id={`note-tooltip-${row?.bookingId || row?.id || "note"}`}
                className="vh-note-tooltip"
              >
                {tooltipText}
              </Tooltip>
            }
          >
            <span
              className="vh-note-trigger"
              tabIndex={0}
              role="button"
              aria-label={`Note: ${tooltipText}`}
            >
              <span>Yes</span>
              <span className="vh-note-icon">
                <img src={infoBootsIcon} alt="info icon" />
              </span>
            </span>
          </OverlayTrigger>
        );
      },
    },
    {
      dataField: "statusText",
      text: "Status",
    },
    {
      dataField: "reportedDate",
      text: "Status date",
    },
    {
      dataField: "actionButton",
      text: "Action",
      render: (value) => value,
    },
  ];

  if (!isPageReady || storeLocatorLoading || isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" className="my-custom-spinner" />
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <LoggedInLayout
      buttonLabel="Logout"
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
      isVhlogin={isVhLogin}
      vitalityLayout={isVhLogin}
    >
      {isVhLogin ? (
        <PageLayout
          heading="Select a service"
          subHeading={
            <>
              To begin a registered service, click the{" "}
              <span className="boots-bold">‘Select’</span> button.
            </>
          }
        >
          {" "}
          <div className="vh-patient-details-page">
            <div className="container">
              {serverError && (
                <span className="server-error">{serverError}</span>
              )}
              <div className="row">
                <div className={`col-md-12 p-0`}>
                  <DetailCard
                    heading="Patient details"
                    data={displayUserInfo}
                    fields={fields}
                  />
                  <VHConfirmationPopup
                    warningText={
                      <span>
                        <b>Please confirm the correct service</b>
                        <br />
                        The patient may have multiple bookings for different
                        services. Please check the service type and date before
                        selecting the record to ensure you are proceeding with
                        the correct service.
                      </span>
                    }
                    showIcon={false}
                    className="mb-3"
                  />
                  <div className="table-responsive">
                    <VitalityDuplicatedTable
                      data={modifiedBookings}
                      pagination={true}
                      sort={false}
                      columns={bookingColumns}
                      statusLabel="Select"
                      pageSize={4}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageLayout>
      ) : (
        <div className="patient-details-page">
          <div className="container">
            {serverError && <span className="server-error">{serverError}</span>}
            <div className="row">
              <div className={`col-md-12 p-0`}>
                <SearchForm
                  colStructure={3}
                  data={displayUserInfo}
                  fields={fields}
                  title={
                    selectedService === "healthCare"
                      ? "Select an appointment"
                      : ""
                  }
                  subtitle={
                    <div className="subtitle-patient-appointment">
                      To select a registered appointment, click the{" "}
                      <span className="bold-ageo">
                        Registered: Select Service
                      </span>{" "}
                      button under Status.
                    </div>
                  }
                  child={
                    <div className="table-responsive">
                      <Table
                        data={modifiedBookings}
                        pagination={true}
                        search={true}
                        sort={false}
                      />
                    </div>
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="container p-0">
                  <div className="row">
                    <div className="col-md-12 patient-details-back-btn mt-3">
                      <Primary
                        className="btn new-back-btn mx-2 mt-5"
                        label="Back"
                        onClick={handleBackClick}
                        size="small"
                        variant="outline"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </LoggedInLayout>
  );
}
export default PatientDetails;
